import { CommonModule } from '@angular/common';
import { afterNextRender, ChangeDetectorRef, Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  heroBanknotes,
  heroBuildingStorefront,
  heroCalendarDays,
  heroCheckCircle,
  heroCircleStack,
  heroCommandLine,
  heroComputerDesktop,
  heroCreditCard,
  heroDocumentText,
  heroHashtag,
  heroIdentification,
  heroReceiptPercent,
  heroServerStack,
  heroShieldCheck,
  heroSignal,
  heroTag,
  heroUser
} from '@ng-icons/heroicons/outline';
import { finalize } from 'rxjs';
import { IsoTransactionRow, PageResponse, TransactionApi, TransactionFilterValue } from '../../core/transaction-api';
import { brandOptions, isoEntryModeOptions, isoProcessingOptions, isoStatusOptions } from '../../shared/options';
import { brandImage, dateTime, moneyFromCents, statusClass, text } from '../../shared/formatters';

type IsoDetailTabId = 'operation' | 'payment' | 'card' | 'merchant' | 'response';

type IsoDetailField = {
  key: string;
  label: string;
  icon: string;
};

type IsoDetailTab = {
  id: IsoDetailTabId;
  label: string;
  icon: string;
  fields: IsoDetailField[];
};

@Component({
  selector: 'app-transactions-iso',
  imports: [CommonModule, FormsModule, NgIcon],
  templateUrl: './transactions-iso.html',
  providers: [provideIcons({
    heroBanknotes,
    heroBuildingStorefront,
    heroCalendarDays,
    heroCheckCircle,
    heroCircleStack,
    heroCommandLine,
    heroComputerDesktop,
    heroCreditCard,
    heroDocumentText,
    heroHashtag,
    heroIdentification,
    heroReceiptPercent,
    heroServerStack,
    heroShieldCheck,
    heroSignal,
    heroTag,
    heroUser
  })]
})
export class TransactionsIso {
  readonly brands = brandOptions;
  readonly statuses = isoStatusOptions;
  readonly entryModes = isoEntryModeOptions;
  readonly processingCodes = isoProcessingOptions;
  readonly pageSizes = [10, 25, 50, 100];

  rows: IsoTransactionRow[] = [];
  filters: Record<string, TransactionFilterValue> = {};
  page = 0;
  size = 10;
  total = 0;
  loading = false;
  error = '';
  detail?: Record<string, unknown>;
  detailLoading = false;
  activeDetailTab: IsoDetailTabId = 'operation';
  filtersOpen = false;

  readonly detailTabs: IsoDetailTab[] = [
    {
      id: 'operation',
      label: 'Operacion',
      icon: '#',
      fields: [
        { key: 'id', label: 'ID', icon: '#' },
        { key: 'transactionId', label: 'Transaccion', icon: 'TX' },
        { key: 'mti', label: 'MTI', icon: 'MT' },
        { key: 'localTransactionDateTime', label: 'Fecha', icon: 'FE' },
        { key: 'transactionStatus', label: 'Estado', icon: 'ST' },
        { key: 'processingType', label: 'Processing', icon: 'PC' },
        { key: 'posEntryModeType', label: 'Entry mode', icon: 'EM' },
        { key: 'stan', label: 'STAN', icon: 'SN' },
        { key: 'ticketNumber', label: 'Ticket', icon: 'TK' },
        { key: 'fallback', label: 'Fallback', icon: 'FB' }
      ]
    },
    {
      id: 'payment',
      label: 'Pago',
      icon: '$',
      fields: [
        { key: 'amountTransaction', label: 'Monto', icon: '$' },
        { key: 'aditionalAmountTransaction', label: 'Monto adicional', icon: 'AD' },
        { key: 'currencyTransaction', label: 'Moneda', icon: 'MO' },
        { key: 'transactionProcessData.authorizationCode', label: 'Autorizacion', icon: 'AU' },
        { key: 'transactionProcessData.referenceNumber', label: 'Referencia', icon: 'RF' },
        { key: 'batchNumber', label: 'Batch', icon: 'BA' }
      ]
    },
    {
      id: 'card',
      label: 'Tarjeta',
      icon: 'CC',
      fields: [
        { key: 'cardData.bin', label: 'BIN', icon: 'BI' },
        { key: 'cardData.l4d', label: 'Ultimos 4', icon: 'L4' },
        { key: 'cardData.cardHolderName', label: 'Titular', icon: 'TH' },
        { key: 'installmentData.plan', label: 'Plan', icon: 'PL' },
        { key: 'installmentData.installmentNumber', label: 'Cuotas', icon: 'CU' }
      ]
    },
    {
      id: 'merchant',
      label: 'Comercio',
      icon: 'CO',
      fields: [
        { key: 'terminalData.terminalIdentification', label: 'Terminal', icon: 'TE' },
        { key: 'merchantData.id', label: 'Merchant ID', icon: 'ID' },
        { key: 'merchantData.cardAcceptorIdentificationCode', label: 'Card acceptor', icon: 'CA' },
        { key: 'merchantData.merchantName', label: 'Comercio', icon: 'CM' },
        { key: 'merchantData.mcc', label: 'MCC', icon: 'MC' },
        { key: 'merchantData.brand', label: 'Marca', icon: 'BR' },
        { key: 'merchantData.processingType', label: 'Tipo proceso', icon: 'TP' }
      ]
    },
    {
      id: 'response',
      label: 'Respuesta',
      icon: 'RC',
      fields: [
        { key: 'transactionProcessData.id', label: 'Process ID', icon: 'ID' },
        { key: 'transactionProcessData.mti', label: 'MTI respuesta', icon: 'MT' },
        { key: 'transactionProcessData.responseCode', label: 'Codigo respuesta', icon: 'RC' },
        { key: 'transactionProcessData.transactionDateTime', label: 'Fecha respuesta', icon: 'FR' },
        { key: 'transactionProcessData.printData', label: 'Print data', icon: 'PD' },
        { key: 'transactionProcessData.originalReferenceNumber', label: 'Referencia original', icon: 'RO' },
        { key: 'transactionProcessData.originalTransactionDateTime', label: 'Fecha original', icon: 'FO' },
        { key: 'transactionProcessData.iccData', label: 'ICC data', icon: 'IC' },
        { key: 'transactionProcessData.iccIssuerData', label: 'ICC issuer', icon: 'II' }
      ]
    }
  ];

  readonly text = text;
  readonly dateTime = dateTime;
  readonly moneyFromCents = moneyFromCents;
  readonly statusClass = statusClass;
  readonly brandImage = brandImage;
  readonly iconMap: Record<string, string> = {
    '#': 'heroHashtag',
    TX: 'heroIdentification',
    MT: 'heroSignal',
    FE: 'heroCalendarDays',
    ST: 'heroShieldCheck',
    PC: 'heroCommandLine',
    EM: 'heroCreditCard',
    SN: 'heroHashtag',
    TK: 'heroReceiptPercent',
    FB: 'heroCheckCircle',
    '$': 'heroBanknotes',
    AD: 'heroBanknotes',
    MO: 'heroBanknotes',
    AU: 'heroShieldCheck',
    RF: 'heroDocumentText',
    BA: 'heroCircleStack',
    CC: 'heroCreditCard',
    BI: 'heroCreditCard',
    L4: 'heroCreditCard',
    TH: 'heroUser',
    PL: 'heroReceiptPercent',
    CU: 'heroReceiptPercent',
    CO: 'heroBuildingStorefront',
    TE: 'heroComputerDesktop',
    ID: 'heroIdentification',
    CA: 'heroIdentification',
    CM: 'heroBuildingStorefront',
    MC: 'heroTag',
    BR: 'heroTag',
    TP: 'heroServerStack',
    RC: 'heroCheckCircle',
    FR: 'heroCalendarDays',
    PD: 'heroDocumentText',
    RO: 'heroDocumentText',
    FO: 'heroCalendarDays',
    IC: 'heroServerStack',
    II: 'heroServerStack'
  };

  constructor(
    private readonly api: TransactionApi,
    private readonly cdr: ChangeDetectorRef
  ) {
    afterNextRender(() => this.load());
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.total / this.size));
  }

  load(): void {
    this.loading = true;
    this.error = '';
    this.api
      .getIsoTransactions({ page: this.page, size: this.size, search: '', filters: this.cleanFilters() })
      .pipe(finalize(() => {
        this.loading = false;
        this.cdr.markForCheck();
      }))
      .subscribe({
        next: (response: PageResponse<IsoTransactionRow>) => {
          this.rows = response.data ?? [];
          this.total = response.recordsFiltered ?? response.recordsTotal ?? 0;
          this.cdr.markForCheck();
        },
        error: error => {
          console.error('Error loading ISO transactions', error);
          this.error = 'No se pudieron cargar las transacciones ISO.';
          this.cdr.markForCheck();
        }
      });
  }

  applyFilters(): void {
    this.page = 0;
    this.load();
  }

  clearFilters(): void {
    this.filters = {};
    this.page = 0;
    this.load();
  }

  changePage(delta: number): void {
    if (this.loading) return;
    this.goToPage(this.page + delta);
  }

  goToPage(page: number): void {
    if (this.loading) return;
    const nextPage = Math.min(Math.max(page, 0), this.totalPages - 1);
    if (nextPage === this.page) return;
    this.page = nextPage;
    this.load();
  }

  visiblePages(): Array<number | 'ellipsis'> {
    const total = this.totalPages;
    const current = this.page;
    if (total <= 7) {
      return Array.from({ length: total }, (_, index) => index);
    }

    const pages = new Set<number>([0, total - 1]);
    for (let index = current - 1; index <= current + 1; index++) {
      if (index > 0 && index < total - 1) {
        pages.add(index);
      }
    }

    const sortedPages = Array.from(pages).sort((a, b) => a - b);
    return sortedPages.flatMap((item, index) => {
      const previous = sortedPages[index - 1];
      return index > 0 && item - previous > 1 ? ['ellipsis' as const, item] : [item];
    });
  }

  openDetail(id: number): void {
    this.detail = undefined;
    this.detailLoading = true;
    this.activeDetailTab = 'operation';
    this.api
      .getIsoDetail(id)
      .pipe(finalize(() => {
        this.detailLoading = false;
        this.cdr.markForCheck();
      }))
      .subscribe({
        next: detail => {
          this.detail = detail;
          this.cdr.markForCheck();
        },
        error: () => {
          this.detail = { error: 'No se pudo cargar el detalle ISO.' };
          this.cdr.markForCheck();
        }
      });
  }

  closeDetail(): void {
    this.detail = undefined;
  }

  detailItems(tab: IsoDetailTab): IsoDetailField[] {
    if (!this.detail) return [];
    return tab.fields.filter(field => this.hasDetailValue(field.key));
  }

  detailValue(key: string): unknown {
    return key.split('.').reduce<unknown>((value, segment) => {
      if (value && typeof value === 'object') {
        return (value as Record<string, unknown>)[segment];
      }
      return undefined;
    }, this.detail);
  }

  formatDetailValue(key: string): string {
    const value = this.detailValue(key);
    if (key === 'amountTransaction' || key === 'aditionalAmountTransaction') return moneyFromCents(value);
    if (key.toLowerCase().includes('date') || key.toLowerCase().includes('time')) return dateTime(value);
    if (value && typeof value === 'object') {
      const record = value as Record<string, unknown>;
      const code = record['code'];
      const description = record['description'];
      if (code && description) return `${text(code)} - ${text(description)}`;
      if (description) return text(description);
      if (code) return text(code);
    }
    return text(value);
  }

  detailIcon(icon: string): string {
    return this.iconMap[icon] ?? 'heroTag';
  }

  activeDetailItems(): IsoDetailField[] {
    const tab = this.detailTabs.find(item => item.id === this.activeDetailTab);
    return tab ? this.detailItems(tab) : [];
  }

  private hasDetailValue(key: string): boolean {
    const value = this.detailValue(key);
    return value !== null && value !== undefined && value !== '';
  }

  private cleanFilters(): Record<string, TransactionFilterValue> {
    return Object.fromEntries(
      Object.entries(this.filters).filter(([, value]) =>
        Array.isArray(value) ? value.length > 0 : value !== undefined && value !== null && value !== ''
      )
    );
  }
}
